import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Button, Dropdown, Menu, message, Modal, Progress, Result, Spin, Tooltip, Upload } from "antd";

import V2PageHeader from "../../v2/components/V2PageHeader";
import V2EmptyState from "../../v2/components/V2EmptyState";
import { StoreContext } from "../../context";
import { errorMessage } from "../../helpers";
import {
  HISTORY_RANGES,
  MAX_FILE_SIZE_MB,
  UPLOAD_STATUS,
  filterByRange,
  getStockUpload,
  getStockUploadHistory,
  getStoreName,
  uploadStockFile,
} from "../../helpers/stockUploadApi";
import { REQUIRED_COLUMNS_LABEL, checkStockFile, shortUploadError } from "./stockFileFormat";
import chevronDown from "./assets/chevron-down.svg";

import "../../v2/tokens/tokens.css";
import "./index.css";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const POLL_INTERVAL_MS = 3000;
// ~5 minutes; past that the page stops asking and says to check back later.
const MAX_POLLS = 100;
// Give up after this many failed status checks in a row (not on the first blip).
const MAX_POLL_FAILURES = 5;
// An upload still "processing" after this long is stuck server-side; don't poll it.
const STALE_PROCESSING_MINUTES = 30;
const HISTORY_PAGE_SIZE = 20;

const RANGE_OPTIONS = [
  { key: HISTORY_RANGES.TODAY, label: "Today" },
  { key: HISTORY_RANGES.LAST_7_DAYS, label: "Last 7 days" },
  { key: HISTORY_RANGES.LAST_30_DAYS, label: "Last 30 days" },
  { key: HISTORY_RANGES.ALL, label: "All" },
];

const STATUS_CELLS = {
  [UPLOAD_STATUS.PROCESSING]: { label: "Processing", className: "stock-upload-status-processing" },
  [UPLOAD_STATUS.ACTIVE]: { label: "Active", className: "stock-upload-status-active" },
  [UPLOAD_STATUS.REPLACED]: { label: "Replaced", className: "" },
  [UPLOAD_STATUS.FAILED]: { label: "Failed", className: "stock-upload-status-failed" },
};

const formatDateTime = (value) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "—");

const formatCount = (count) => Number(count || 0).toLocaleString("en-IN");

const fallbackStoreLabel = (storeId) => `Store #${String(storeId).padStart(2, "0")}`;

const isMatchless = (upload) => upload?.status === UPLOAD_STATUS.ACTIVE && upload.totalRows > 0 && !upload.matchedRows;

const isFreshProcessing = (upload) =>
  upload?.status === UPLOAD_STATUS.PROCESSING &&
  moment().diff(moment(upload.uploadedAt), "minutes") < STALE_PROCESSING_MINUTES;

const checkFileBasics = (file) => {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "Only .xlsx, .xls or .csv files can be uploaded.";
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `The file is larger than ${MAX_FILE_SIZE_MB} MB.`;
  }
  return null;
};

export default function StockUpload() {
  const { storeId } = useContext(StoreContext);
  const [storeName, setStoreName] = useState(null);
  const [range, setRange] = useState(HISTORY_RANGES.TODAY);
  const [activeFile, setActiveFile] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [checkingName, setCheckingName] = useState(null);
  const [uploading, setUploading] = useState(null); // { name, percent }
  const [fileError, setFileError] = useState(null); // { fileName, message }
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);

  // Requests can resolve after the user has navigated away or switched store;
  // only the latest request for the current store may update the page.
  const mounted = useRef(true);
  const currentStoreId = useRef(storeId);
  const historyRequest = useRef(0);
  currentStoreId.current = storeId;
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const label = storeName || (storeId ? fallbackStoreLabel(storeId) : "");

  const loadHistory = useCallback(async () => {
    if (!storeId) return;
    const requestId = ++historyRequest.current;
    setLoading(true);
    const res = await getStockUploadHistory(storeId);
    if (!mounted.current || requestId !== historyRequest.current) return;
    setLoadFailed(!res);
    setActiveFile(res?.active || null);
    setUploads(res?.history || []);
    setLoading(false);
  }, [storeId]);

  useEffect(() => {
    setActiveFile(null);
    setUploads([]);
    setFileError(null);
    setVisibleCount(HISTORY_PAGE_SIZE);
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    setStoreName(null);
    getStoreName(storeId).then((name) => {
      if (!cancelled && mounted.current) setStoreName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  // Uploads are processed in the background: follow the newest in-flight one
  // (just uploaded, or still running from earlier) until it settles.
  const processing = uploads.find(isFreshProcessing);
  const processingId = processing?.id;
  useEffect(() => {
    if (!processingId) return undefined;
    let polls = 0;
    let failures = 0;
    let stopped = false;
    const timer = setInterval(async () => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        message.info("The stock file is still processing. Check back in a few minutes.");
        return;
      }
      const detail = await getStockUpload(processingId);
      if (stopped || !mounted.current) return;
      if (!detail) {
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          clearInterval(timer);
          errorMessage("Couldn't check the stock file's progress. Refresh the page to try again.");
        }
        return;
      }
      failures = 0;
      if (detail.status === UPLOAD_STATUS.PROCESSING) return;
      clearInterval(timer);
      if (detail.status === UPLOAD_STATUS.FAILED) {
        errorMessage(detail.failReason || `${detail.fileName} couldn't be processed.`);
      } else if (isMatchless(detail)) {
        errorMessage(`No products in ${detail.fileName} matched the catalogue. Check it's the right store's stock export.`);
      } else {
        message.success(`${detail.fileName} is now the active stock file.`);
      }
      loadHistory();
    }, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [processingId, loadHistory]);

  const upload = async (file) => {
    const uploadStoreId = storeId;
    setUploading({ name: file.name, percent: 0 });
    const res = await uploadStockFile(uploadStoreId, file, {
      onProgress: (percent) => mounted.current && setUploading({ name: file.name, percent }),
    });
    if (!mounted.current) return;
    // The user switched store mid-upload: the new store's page owns the view now.
    if (currentStoreId.current !== uploadStoreId) {
      setUploading(null);
      return;
    }
    if (!res.ok) {
      setUploading(null);
      setFileError({ fileName: file.name, message: shortUploadError(res.message) });
      return;
    }
    message.success(`${file.name} uploaded (${formatCount(res.totalRows)} rows). Processing now…`);
    // Stay locked until the history shows the new "processing" row, so the
    // drop zone doesn't briefly accept a second file.
    await loadHistory();
    if (mounted.current) setUploading(null);
  };

  const confirmAndUpload = (file) => {
    Modal.confirm({
      title: activeFile ? "Replace the current stock file?" : "Upload this stock file?",
      content: (
        <>
          <p>
            {activeFile
              ? `${file.name} will replace ${activeFile.fileName} as the active stock file for ${label}.`
              : `${file.name} will become the active stock file for ${label}.`}
          </p>
          <p className="stock-upload-confirm-warning">Products that aren't in this file will be deactivated.</p>
        </>
      ),
      okText: activeFile ? "Upload and replace" : "Upload",
      onOk: () => upload(file),
    });
  };

  const handleFile = (file) => {
    setFileError(null);
    const basicError = checkFileBasics(file);
    if (basicError) {
      setFileError({ fileName: file.name, message: basicError });
      return false;
    }
    setCheckingName(file.name);
    checkStockFile(file)
      // An unexpected failure here must not leave the drop zone stuck on
      // "Checking…"; the backend still validates the file.
      .catch(() => null)
      .then((formatError) => {
        if (!mounted.current) return;
        setCheckingName(null);
        if (formatError) {
          setFileError({ fileName: file.name, message: formatError });
          return;
        }
        confirmAndUpload(file);
      });
    // Uploads run through upload() — stop antd from posting the file itself.
    return false;
  };

  if (!storeId) {
    return (
      <div className="stock-upload">
        <V2PageHeader
          title="Stock Upload"
          subtitle="Upload the latest stock file for your store. Each new upload replaces the previous one."
        />
        <div className="stock-upload-centered">
          <V2EmptyState
            title="No store selected"
            message="Pick a store at the top, or ask your admin to link your login to a store."
          />
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="stock-upload">
        <Result
          status="error"
          title="Couldn't load stock uploads"
          extra={<Button onClick={loadHistory}>Try again</Button>}
        />
      </div>
    );
  }

  const rangeLabel = RANGE_OPTIONS.find((option) => option.key === range)?.label;
  const rangeMenu = (
    <Menu
      selectedKeys={[range]}
      onClick={({ key }) => {
        setRange(key);
        setVisibleCount(HISTORY_PAGE_SIZE);
      }}
    >
      {RANGE_OPTIONS.map((option) => (
        <Menu.Item key={option.key}>{option.label}</Menu.Item>
      ))}
    </Menu>
  );

  const rangeUploads = filterByRange(uploads, range);
  const shownUploads = rangeUploads.slice(0, visibleCount);
  const busy = Boolean(checkingName || uploading || processing);

  const historySubtitle = processing
    ? `${label} · processing ${processing.fileName}`
    : `${label} · ${activeFile ? "latest upload is live" : "no active stock file"}`;

  const emptyHistoryMessage = !uploads.length
    ? "Uploaded stock files will appear here."
    : activeFile
    ? `The active file was uploaded on ${formatDateTime(activeFile.uploadedAt)}. Try a longer range.`
    : "Try a longer range to see earlier uploads.";

  const dropzoneContent = () => {
    if (uploading) {
      // At 100% the file is sent, but the server still reads it before replying.
      const sent = uploading.percent >= 100;
      return (
        <div className="stock-upload-dropzone-content">
          <p className="stock-upload-dropzone-title">
            {sent ? `Checking ${uploading.name} on the server…` : `Uploading ${uploading.name}…`}
          </p>
          {sent ? (
            <Spin />
          ) : (
            <Progress
              className="stock-upload-progress"
              percent={uploading.percent}
              size="small"
              strokeColor="var(--rapsap-blue)"
            />
          )}
        </div>
      );
    }
    if (checkingName || processing) {
      return (
        <div className="stock-upload-dropzone-content">
          <Spin />
          <p className="stock-upload-dropzone-title">
            {checkingName ? `Checking ${checkingName}…` : `Processing ${processing.fileName}…`}
          </p>
          {processing && (
            <p className="stock-upload-dropzone-hint">You can upload another file once this one is done.</p>
          )}
        </div>
      );
    }
    return (
      <div className="stock-upload-dropzone-content">
        <p className="stock-upload-dropzone-arrow" aria-hidden="true">
          ↑
        </p>
        <p className="stock-upload-dropzone-title">Drag and drop your stock file here</p>
        <p className="stock-upload-dropzone-hint">.xlsx, .xls or .csv</p>
        <p className="stock-upload-dropzone-columns">Needs columns: {REQUIRED_COLUMNS_LABEL}</p>
        {/* Clicks bubble to the dragger, which opens the file picker. */}
        <button type="button" className="stock-upload-browse">
          Browse files
        </button>
      </div>
    );
  };

  return (
    <div className="stock-upload">
      <V2PageHeader
        title="Stock Upload"
        subtitle="Upload the latest stock file for your store. Each new upload replaces the previous one."
      />

      <section className="stock-upload-card stock-upload-current" aria-label="Current stock file">
        <Spin spinning={loading && !uploads.length}>
          <div className="stock-upload-current-grid">
            <div className="stock-upload-field">
              <p className="stock-upload-field-label">Active stock file</p>
              <p className="stock-upload-field-value" title={activeFile?.fileName}>
                {activeFile?.fileName || "—"}
              </p>
            </div>
            <div className="stock-upload-field">
              <p className="stock-upload-field-label">Last updated</p>
              <p className="stock-upload-field-value">{formatDateTime(activeFile?.uploadedAt)}</p>
            </div>
            <div className="stock-upload-field">
              <p className="stock-upload-field-label">Uploaded by</p>
              <p className="stock-upload-field-value">{activeFile?.uploadedBy || "—"}</p>
            </div>
          </div>
          {activeFile && activeFile.totalRows > 0 && (
            <p className="stock-upload-summary">
              {formatCount(activeFile.matchedRows)} matched · {formatCount(activeFile.skippedRows)} skipped ·{" "}
              {formatCount(activeFile.invalidRows)} invalid · {formatCount(activeFile.deactivatedRows)} deactivated
              <span className="stock-upload-summary-total"> of {formatCount(activeFile.totalRows)} rows</span>
            </p>
          )}
          {isMatchless(activeFile) && (
            <p className="stock-upload-alert" role="alert">
              No products in this file matched the catalogue, so this store's other products were deactivated. Upload
              the store's correct stock export.
            </p>
          )}
        </Spin>
      </section>

      <section className="stock-upload-card">
        <h2 className="stock-upload-heading">Upload new stock file</h2>
        <Upload.Dragger
          className="stock-upload-dropzone"
          accept={ACCEPTED_EXTENSIONS.join(",")}
          multiple={false}
          showUploadList={false}
          beforeUpload={handleFile}
          disabled={busy}
        >
          {dropzoneContent()}
        </Upload.Dragger>
        {fileError && (
          <div className="stock-upload-file-error" role="alert">
            <p className="stock-upload-file-error-message">{fileError.message}</p>
            <p className="stock-upload-file-error-file">{fileError.fileName}</p>
          </div>
        )}
      </section>

      <section className="stock-upload-card">
        <div className="stock-upload-history-heading">
          <div className="stock-upload-history-titles">
            <h2 className="stock-upload-heading">Upload history</h2>
            <p className="stock-upload-subtle">{historySubtitle}</p>
          </div>
          <Dropdown overlay={rangeMenu} trigger={["click"]} placement="bottomRight">
            <button type="button" className="stock-upload-range">
              {rangeLabel}
              <img src={chevronDown} alt="" width="20" height="20" className="stock-upload-range-icon" />
            </button>
          </Dropdown>
        </div>

        <Spin spinning={loading}>
          {rangeUploads.length === 0 && !loading ? (
            <div className="stock-upload-centered">
              <V2EmptyState title="No uploads in this period" message={emptyHistoryMessage} />
            </div>
          ) : (
            <>
              <table className="stock-upload-table">
                <thead>
                  <tr>
                    <th className="stock-upload-col-name">File name</th>
                    <th className="stock-upload-col-date">Date &amp; time</th>
                    <th className="stock-upload-col-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shownUploads.map((row) => {
                    const status = STATUS_CELLS[row.status];
                    return (
                      <tr key={row.id ?? `${row.fileName}-${row.uploadedAt}`}>
                        <td className="stock-upload-col-name" title={row.fileName}>
                          {row.fileName}
                        </td>
                        <td className="stock-upload-col-date">{formatDateTime(row.uploadedAt)}</td>
                        <td className={`stock-upload-col-status ${status.className}`.trim()}>
                          {row.status === UPLOAD_STATUS.FAILED && row.failReason ? (
                            <Tooltip title={row.failReason}>{status.label}</Tooltip>
                          ) : (
                            status.label
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rangeUploads.length > shownUploads.length && (
                <button
                  type="button"
                  className="stock-upload-show-more"
                  onClick={() => setVisibleCount((count) => count + HISTORY_PAGE_SIZE)}
                >
                  Show {Math.min(HISTORY_PAGE_SIZE, rangeUploads.length - shownUploads.length)} more
                </button>
              )}
            </>
          )}
        </Spin>
      </section>
    </div>
  );
}
